/** Every tenant operates in Colombia: dates are shown, captured and bucketed in this zone. */
export const BUSINESS_TIME_ZONE = 'America/Bogota'
// shortcut: fixed offset to turn wall time into an instant; Colombia has no DST. Upgrade to zone math if a tenant runs elsewhere.
const BUSINESS_UTC_OFFSET = '-05:00'

const partsFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
})

function parseInstant(value: string | number | Date) {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Formats an instant in the business zone, so SSR (UTC) and every browser render the same wall time. */
export function formatBusinessDate(value: string | number | Date, locale: string, options: Intl.DateTimeFormatOptions) {
  const date = parseInstant(value)
  return date ? new Intl.DateTimeFormat(locale, { ...options, timeZone: BUSINESS_TIME_ZONE }).format(date) : String(value)
}

/** `YYYY-MM-DDTHH:mm` value for a `datetime-local` input, in business-zone wall time. */
export function toBusinessDateTimeInput(date: Date) {
  const parts = Object.fromEntries(partsFormatter.formatToParts(date).map(part => [part.type, part.value]))
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

/** `YYYY-MM-DD` of the business-zone calendar day containing the instant. */
export function toBusinessDateInput(date: Date) {
  return toBusinessDateTimeInput(date).slice(0, 10)
}

export function isoInstantToBusinessDateTimeInput(value: string | null) {
  const date = value ? parseInstant(value) : null
  return date ? toBusinessDateTimeInput(date) : ''
}

/** Business-zone wall time (`YYYY-MM-DDTHH:mm[:ss[.sss]]`) to an ISO instant; null when invalid. */
export function businessDateTimeInputToIso(value: string) {
  const trimmed = value.trim()
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?$/.test(trimmed)) return null
  return parseInstant(`${trimmed.length === 16 ? `${trimmed}:00` : trimmed}${BUSINESS_UTC_OFFSET}`)?.toISOString() ?? null
}

/** First or last instant of a business-zone calendar day (`YYYY-MM-DD`). */
export function businessDayBoundaryIso(day: string, boundary: 'start' | 'end') {
  return /^\d{4}-\d{2}-\d{2}$/.test(day)
    ? businessDateTimeInputToIso(`${day}T${boundary === 'start' ? '00:00:00.000' : '23:59:59.999'}`)
    : null
}
